import * as Yup from "yup";
import { FormikProvider, useFormik } from "formik";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { IInvoice, Item } from "@/types/invoice.type";
import {
  PurchaseOrderItem,
  searchPurchaseOrderByCode,
} from "@/common/services/invoice.api";
import { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";

import InputFormik from "@/common/components/form/InputFormik";
import SelectFormik from "@/common/components/form/SelectFormik";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { Button } from "@/components/ui/button";

const validationSchema = Yup.object().shape({
  purchaseOrderCode: Yup.string().required("O código da OC é obrigatório"),
  selectedPurchaseOrderItemId: Yup.string().required(
    "É obrigatório selecionar um item da OC"
  ),
});

type FoundItem = { _id: string; description: string };

type FormAddPurchaseOrderProps = {
  invoiceId: string;
  item: Item;
  onSave: (updatedInvoice: IInvoice) => void;
  onClose: () => void;
};

export function FormAddPurchaseOrder({
  invoiceId,
  item,
  onSave,
  onClose,
}: FormAddPurchaseOrderProps) {
  const { notifySuccess, notifyError } = useToastContext();

  const [foundItems, setFoundItems] = useState<FoundItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      purchaseOrderCode: "",
      selectedItemDescription: "",
      selectedPurchaseOrderItemId: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const result = await PurchaseOrderItem(invoiceId, item.numero_item, {
        purchaseOrderCode: values.purchaseOrderCode,
        purchaseOrderItemId: values.selectedPurchaseOrderItemId,
      });

      if (result.ok && result.data) {
        notifySuccess("Item da nota vinculado com sucesso!");
        onSave(result.data);
      } else {
        notifyError(result.message || "Erro ao vincular item.");
      }
      setSubmitting(false);
    },
  });

  const optionLabels = foundItems.map((item) => item.description);

  useEffect(() => {
    const selectedDescription = formik.values.selectedItemDescription;
    if (selectedDescription) {
      const selectedItem = foundItems.find(
        (found) => found.description === selectedDescription
      );
      if (selectedItem) {
        formik.setFieldValue("selectedPurchaseOrderItemId", selectedItem._id);
      }
    } else {
      formik.setFieldValue("selectedPurchaseOrderItemId", "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.selectedItemDescription, foundItems]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (code: string) => {
      if (code.length < 4) {
        setFoundItems([]);
        setSearchError(null);
        return;
      }
      setIsSearching(true);
      setSearchError(null);
      formik.setFieldValue("selectedItemDescription", "");

      const result = await searchPurchaseOrderByCode(code);
      if (result.ok && result.data && result.data.length > 0) {
        setFoundItems(result.data);
      } else {
        setFoundItems([]);
        setSearchError(result.message || "Nenhuma OC encontrada ou sem itens.");
      }
      setIsSearching(false);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(formik.values.purchaseOrderCode);
  }, [formik.values.purchaseOrderCode, debouncedSearch]);

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <div className="p-4 space-y-4">
          <p>
            <strong className="text-black">Item da Nota:</strong>{" "}
            {item.descricao}
          </p>

          <InputFormik
            label="Código da Ordem de Compra"
            name="purchaseOrderCode"
            placeholder="Digite para buscar a OC..."
            required
          />

          {isSearching && (
            <p className="text-sm text-gray-500">Buscando OC...</p>
          )}
          {searchError && <p className="text-sm text-red-500">{searchError}</p>}

          {foundItems.length > 0 && (
            <SelectFormik
              label="Selecione o Item da Ordem de Compra correspondente"
              name="selectedItemDescription"
              options={optionLabels}
              required
            />
          )}

          <div className="flex justify-end mt-4 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={formik.isSubmitting}
            >
              Cancelar
            </Button>
            <ButtonWithLoader type="submit" isLoading={formik.isSubmitting}>
              Vincular Item
            </ButtonWithLoader>
          </div>
        </div>
      </form>
    </FormikProvider>
  );
}
